/**
 * MobileAppBanner — top-sticky "Open in app" banner for mobile web visitors.
 *
 * Rendered globally; auto-hides on desktop, on routes that don't deep-link
 * cleanly (admin/auth/onboarding), and after the user dismisses it (30 days).
 *
 * Click flow:
 *   • iOS:     window.location = `evoluras://...`, then 1.5s timeout → App Store
 *              (if the app caught the scheme, the timeout is cancelled by the
 *               page being backgrounded — see `pagehide` listener).
 *   • Android: a single intent:// URL with `S.browser_fallback_url` baked in
 *              so the OS handles the install-store fallback natively.
 *
 * Notes for ops:
 *   • App Store ID, Play Store URL, deep-link scheme, Android package name
 *     are configurable via REACT_APP_* env vars (with sensible defaults).
 *   • `applinks` paths in `apple-app-site-association` should match the
 *     SPA paths we map to deep-link paths in `buildDeepLinkPath` below.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";
import axios from "axios";

const DISMISS_KEY = "evoluras.app_banner_dismissed_at";
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const API = process.env.REACT_APP_BACKEND_URL;

// Reasonable defaults — override at build time via .env if/when the app
// ships under different identifiers. The store URLs are intentionally the
// generic "search" pages so the banner still degrades gracefully if the
// real listing isn't live yet.
const APP_STORE_URL =
  process.env.REACT_APP_IOS_APP_STORE_URL
  || "https://apps.apple.com/app/evoluras";
const PLAY_STORE_URL =
  process.env.REACT_APP_ANDROID_PLAY_STORE_URL
  || "https://play.google.com/store/apps/details?id=com.evoluras.app";
const DEEP_LINK_SCHEME =
  process.env.REACT_APP_DEEP_LINK_SCHEME || "evoluras";
const ANDROID_PACKAGE =
  process.env.REACT_APP_ANDROID_PACKAGE || "com.evoluras.app";

// SPA paths that should NOT trigger the banner — auth, admin, onboarding
// surfaces don't make sense to deep-link out from. Mirrors the "NOT" list
// in `apple-app-site-association`.
const BLOCKED_PATH_PREFIXES = [
  "/admin",
  "/auth",
  "/login",
  "/logout",
  "/onboarding",
  "/privacy",
  "/sacsaae",
  "/api",
  "/.well-known",
];

// Reserved single-segment paths that look like usernames but aren't —
// don't map them to `/u/<handle>` in the deep-link.
const RESERVED_FIRST_SEGMENTS = new Set([
  "dashboard", "tools", "myself", "network", "notifications",
  "profile", "insights", "wheel", "values", "swot", "respond",
  "invite", "u", "p", "i", "c", "welcome", "w",
]);

const detectPlatform = () => {
  if (typeof navigator === "undefined") return "desktop";
  const ua = (navigator.userAgent || navigator.vendor || "").toString();
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return "ios";
  if (/android/i.test(ua)) return "android";
  return "desktop";
};

// Map an SPA path to the equivalent in-app deep-link path. Returns "" for
// the home/root, which tells the click handler to just open the app.
const buildDeepLinkPath = (pathname) => {
  const path = (pathname || "/").replace(/^\/+/, "");
  if (!path) return "";
  // Pre-mapped routes
  if (path.startsWith("u/") || path.startsWith("i/") || path.startsWith("c/")) {
    return path;
  }
  // /<username> or /<username>/gratitude → u/<username>
  const seg = path.split("/")[0];
  if (seg && !RESERVED_FIRST_SEGMENTS.has(seg) && /^[a-zA-Z0-9_.-]+$/.test(seg)) {
    return `u/${seg}`;
  }
  return "";
};

// If the current SPA path is a public profile, return the username — the
// banner uses this to personalize copy ("Open Ana's profile in the app").
// Returns null for non-profile routes.
const extractProfileHandle = (pathname) => {
  const path = (pathname || "/").replace(/^\/+/, "");
  if (!path) return null;
  if (path.startsWith("u/")) {
    const slug = path.split("/")[1];
    return slug || null;
  }
  const seg = path.split("/")[0];
  if (seg && !RESERVED_FIRST_SEGMENTS.has(seg) && /^[a-zA-Z0-9_.-]+$/.test(seg)) {
    return seg;
  }
  return null;
};

const isDismissed = () => {
  try {
    const ts = parseInt(window.localStorage.getItem(DISMISS_KEY) || "0", 10);
    return ts && Date.now() - ts < DISMISS_TTL_MS;
  } catch {
    return false;
  }
};

const MobileAppBanner = () => {
  const location = useLocation();
  const [platform, setPlatform] = useState("desktop");
  const [dismissed, setDismissed] = useState(true); // default true → SSR/desktop never flash
  // When on a public profile, we hydrate `profile` with { firstName, avatar }
  // from the public-profile API so the banner reads "Open Ana's profile in
  // the app" with Ana's avatar — much higher conversion than generic copy.
  const [profile, setProfile] = useState(null);

  // First-render detection. Runs once.
  useEffect(() => {
    const p = detectPlatform();
    setPlatform(p);
    setDismissed(isDismissed());
  }, []);

  // Personalize on route change — only fire on mobile to avoid a useless
  // request on desktop/banner-suppressed sessions.
  useEffect(() => {
    if (platform === "desktop") return;
    const handle = extractProfileHandle(location.pathname);
    if (!handle) {
      setProfile(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/public/profile/${encodeURIComponent(handle)}`);
        if (cancelled || !res?.data) return;
        const name = (res.data.name || "").trim();
        const firstName = name.split(" ")[0] || handle;
        const picture = res.data.picture && /^https?:\/\//.test(res.data.picture)
          ? res.data.picture
          : null;
        setProfile({ firstName, picture, handle });
      } catch {
        // Silent fail → banner falls back to generic copy. The personalize
        // step is enhancement only; never blocks the open-in-app flow.
        setProfile(null);
      }
    })();
    return () => { cancelled = true; };
  }, [platform, location.pathname]);

  // Hide on blocked routes
  const blocked = BLOCKED_PATH_PREFIXES.some((p) =>
    location.pathname === p || location.pathname.startsWith(`${p}/`)
  );

  if (platform === "desktop" || dismissed || blocked) return null;

  const deepPath = buildDeepLinkPath(location.pathname);
  const personalized = !!profile;
  const headline = personalized ? `${profile.firstName} on Evoluras` : "Evoluras";
  const subline = personalized
    ? `Open ${profile.firstName}'s profile in the app`
    : "Open this in the app";
  const avatarSrc = profile?.picture || "/evoluras-icon.png";
  const avatarAlt = personalized ? `${profile.firstName}'s avatar` : "";
  // When the icon is the user's avatar, render it as a circle with a soft
  // violet ring so it visually echoes the cover-page avatar styling.
  const avatarStyle = personalized
    ? {
        boxShadow: "0 0 0 1.5px rgba(167,139,250,0.55), 0 0 12px rgba(167,139,250,0.35)",
      }
    : {
        boxShadow: "0 0 0 1px rgba(255,255,255,0.06)",
      };

  const handleOpen = () => {
    if (platform === "ios") {
      // Try the custom scheme. If the app catches it, the page is backgrounded
      // and the timeout never fires (page becomes hidden before then). If the
      // app isn't installed, the scheme silently fails and we redirect to the
      // App Store. We listen for `pagehide` to cancel the timeout reliably.
      let cancelled = false;
      const onHide = () => { cancelled = true; };
      window.addEventListener("pagehide", onHide, { once: true });
      window.location.href = `${DEEP_LINK_SCHEME}://${deepPath}`;
      window.setTimeout(() => {
        window.removeEventListener("pagehide", onHide);
        if (cancelled) return;
        if (document.hidden) return;
        window.location.href = APP_STORE_URL;
      }, 1500);
    } else if (platform === "android") {
      // Single intent:// URL — Android handles the Play Store fallback
      // natively via S.browser_fallback_url, so no JS timeout needed.
      const fallback = encodeURIComponent(PLAY_STORE_URL);
      const intent =
        `intent://${deepPath}#Intent;scheme=${DEEP_LINK_SCHEME};` +
        `package=${ANDROID_PACKAGE};` +
        `S.browser_fallback_url=${fallback};end`;
      window.location.href = intent;
    }
  };

  const handleDismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch { /* private mode → just hide for this session */ }
    setDismissed(true);
  };

  return (
    <div
      role="region"
      aria-label="Open Evoluras in the mobile app"
      data-testid="mobile-app-banner"
      data-personalized={personalized ? "true" : "false"}
      className="fixed top-0 inset-x-0 z-50 flex items-center gap-3 px-3 py-2.5"
      style={{
        background: "rgba(20, 15, 35, 0.92)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "0 4px 16px rgba(0,0,0,0.28)",
      }}
    >
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss app banner"
        className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors"
        style={{ background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.7)" }}
        data-testid="mobile-app-banner-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      <img
        src={avatarSrc}
        alt={avatarAlt}
        aria-hidden={personalized ? undefined : "true"}
        className={`w-8 h-8 ${personalized ? "rounded-full" : "rounded-lg"} flex-shrink-0 object-cover`}
        style={avatarStyle}
        data-testid="mobile-app-banner-icon"
      />

      <div className="flex-1 min-w-0 leading-tight">
        <p
          className="text-[13px] font-semibold text-white truncate"
          data-testid="mobile-app-banner-headline"
        >
          {headline}
        </p>
        <p
          className="text-[11px] text-white/60 truncate"
          data-testid="mobile-app-banner-subline"
        >
          {subline}
        </p>
      </div>

      <button
        type="button"
        onClick={handleOpen}
        className="inline-flex items-center gap-1 h-9 px-4 rounded-full text-[12px] font-semibold flex-shrink-0 transition-colors"
        style={{
          background: "var(--color-brand-primary, #7c3aed)",
          color: "var(--color-text-on-brand, #ffffff)",
        }}
        data-testid="mobile-app-banner-open"
      >
        Open
        <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  );
};

export default MobileAppBanner;
